import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { analyticsEvents, track } from '@/lib/analytics/posthog';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

type Kind = 'problem' | 'idea' | 'other';

const KINDS: Array<{ value: Kind; label: string; prompt: string }> = [
  { value: 'problem', label: "Something's broken", prompt: 'What were you doing, and what went wrong?' },
  { value: 'idea', label: 'Idea', prompt: 'What would make Light Hunt better for you?' },
  { value: 'other', label: 'Other', prompt: "What's on your mind?" },
];

const MESSAGE_MIN = 5;
const MESSAGE_MAX = 2000;

/** Sends the signed-in user's token when there is one, so the report says who wrote it. */
async function authHeader(): Promise<Record<string, string>> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function sendFeedbackReport(report: {
  kind: Kind;
  message: string;
  email: string;
  page: string;
  website: string;
}): Promise<void> {
  let response: Response;
  try {
    response = await fetch('/api/feedback/report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
      body: JSON.stringify({ ...report, source: 'web' }),
    });
  } catch {
    throw new Error('Could not reach Light Hunt. Check your connection and try again.');
  }
  if (response.ok) return;
  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  throw new Error(body?.error || 'Something went wrong on our side. Try again in a minute.');
}

interface ReportProblemButtonProps {
  className?: string;
  children?: React.ReactNode;
}

/**
 * "Report a problem" link plus its dialog. Works signed out: the report goes
 * to the owner by Slack and email through POST /api/feedback/report.
 */
const ReportProblemButton: React.FC<ReportProblemButtonProps> = ({ className, children }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<Kind>('problem');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [page, setPage] = useState('');
  const [website, setWebsite] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const openDialog = () => {
    setPage(location.pathname);
    setError(null);
    setSent(false);
    setOpen(true);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (message.trim().length < MESSAGE_MIN) {
      setError('Write at least 5 characters so we know what happened.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      await sendFeedbackReport({ kind, message: message.trim(), email: email.trim(), page: page.trim(), website });
      track(analyticsEvents.FEEDBACK_REPORT_SENT, { kind, source: 'web' });
      setSent(true);
      setMessage('');
    } catch (submitError) {
      setError((submitError as Error).message);
    } finally {
      setSending(false);
    }
  };

  const prompt = KINDS.find((k) => k.value === kind)?.prompt ?? '';

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className={cn('text-left text-muted-foreground transition-colors hover:text-foreground', className)}
      >
        {children ?? 'Report a problem'}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-md">
          {sent ? (
            <>
              <DialogHeader>
                <DialogTitle role="status">Sent. Thanks, we read every one.</DialogTitle>
                <DialogDescription className="sr-only">Your report reached the Light Hunt team.</DialogDescription>
              </DialogHeader>
              <div className="flex justify-end">
                <Button type="button" onClick={() => setOpen(false)}>
                  Done
                </Button>
              </div>
            </>
          ) : (
            <form onSubmit={submit} className="space-y-4" noValidate>
              <DialogHeader>
                <DialogTitle>Send feedback</DialogTitle>
                <DialogDescription>A person reads every report. Add your email if you want a reply.</DialogDescription>
              </DialogHeader>

              <fieldset className="space-y-2">
                <legend className="mb-2 text-sm font-medium">What is this about?</legend>
                <div className="grid grid-cols-3 gap-2">
                  {KINDS.map((option) => (
                    <label key={option.value} className="block">
                      <input
                        type="radio"
                        name="feedback-kind"
                        value={option.value}
                        checked={kind === option.value}
                        onChange={() => setKind(option.value)}
                        className="peer sr-only"
                      />
                      <span className="flex h-full min-h-11 cursor-pointer items-center justify-center rounded-md border-2 border-input bg-background px-2 py-2 text-center text-sm leading-tight transition-colors hover:bg-muted peer-checked:border-primary peer-checked:bg-primary/10 peer-checked:font-medium peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background">
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="space-y-2">
                <Label htmlFor="feedback-message">Your message</Label>
                <Textarea
                  id="feedback-message"
                  rows={5}
                  maxLength={MESSAGE_MAX}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder={prompt}
                  aria-describedby={error ? 'feedback-error' : undefined}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="feedback-email">Your email (optional)</Label>
                <Input
                  id="feedback-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                />
                <p className="text-xs text-muted-foreground">So we can reply. We use it for nothing else.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="feedback-page">Page</Label>
                <Input
                  id="feedback-page"
                  value={page}
                  onChange={(event) => setPage(event.target.value)}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">We filled this in. Change it if the problem is somewhere else.</p>
              </div>

              {/* Honeypot: people never see it, bots fill it. */}
              <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
                <label htmlFor="feedback-website">Leave this empty</label>
                <input
                  id="feedback-website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                />
              </div>

              {error && (
                <p id="feedback-error" role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={sending}>
                  Cancel
                </Button>
                <Button type="submit" disabled={sending || !message.trim()}>
                  {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                  Send
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export { ReportProblemButton };
export default ReportProblemButton;
